import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Database, RefreshCw, AlertTriangle, CheckCircle2, TrendingUp } from 'lucide-react';

/**
 * MongoDB storage widget for Super Admin.
 * Auto-refreshes every 60s. Turns amber >75% and red >90% of quota
 * to give early warning before writes get blocked at 100%.
 */
const DbSizeWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [openBreakdown, setOpenBreakdown] = useState(false);
  const [collectionsData, setCollectionsData] = useState(null);
  const [collectionsLoading, setCollectionsLoading] = useState(false);

  const fetchStats = React.useCallback(async () => {
    try {
      setRefreshing(true);
      const res = await api.get('/admin/db-stats');
      setData(res.data);
    } catch (_e) {
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchCollections = React.useCallback(async () => {
    try {
      setCollectionsLoading(true);
      const res = await api.get('/admin/db-stats/collections');
      setCollectionsData(res.data);
    } catch (_e) {
      setCollectionsData({ top_collections: [] });
    } finally {
      setCollectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const t = setInterval(fetchStats, 60_000);
    return () => clearInterval(t);
  }, [fetchStats]);

  const handleOpenBreakdown = () => {
    setOpenBreakdown(true);
    if (!collectionsData) fetchCollections();
  };

  if (loading || !data) return null;

  const { used_pct, data_mb, quota_mb, index_mb, storage_mb, level,
    collections_count, objects_count } = data;
  const top_collections = collectionsData?.top_collections || [];

  const barColor = level === 'critical' ? 'bg-red-500'
    : level === 'warning' ? 'bg-amber-500'
      : 'bg-emerald-500';
  const badgeCls = level === 'critical' ? 'bg-red-100 text-red-800 border-red-200'
    : level === 'warning' ? 'bg-amber-100 text-amber-800 border-amber-200'
      : 'bg-emerald-100 text-emerald-800 border-emerald-200';
  const StatusIcon = level === 'ok' ? CheckCircle2 : AlertTriangle;
  const statusText = level === 'critical' ? 'Critical — writes at risk'
    : level === 'warning' ? 'Warning — approaching quota'
      : 'Healthy';

  return (
    <>
      <Card data-testid="db-size-widget" className={
        `border-l-4 ${level === 'critical' ? 'border-l-red-500' : level === 'warning' ? 'border-l-amber-500' : 'border-l-emerald-500'}`
      }>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4 text-primary" />
              Database Storage
              <Badge variant="outline" className={`text-[10px] ml-2 ${badgeCls}`}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusText}
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              <button
                onClick={handleOpenBreakdown}
                className="text-xs text-primary hover:underline"
                data-testid="db-size-breakdown-btn"
              >
                Breakdown
              </button>
              <Button
                variant="ghost" size="sm"
                onClick={fetchStats} disabled={refreshing}
                title="Refresh"
                data-testid="db-size-refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <div>
              {quota_mb >= 1024 ? (
                <>
                  <span className="text-2xl font-bold font-mono">{(data_mb / 1024).toFixed(2)}</span>
                  <span className="text-muted-foreground"> GB / {(quota_mb / 1024).toFixed(0)} GB</span>
                </>
              ) : (
                <>
                  <span className="text-2xl font-bold font-mono">{data_mb}</span>
                  <span className="text-muted-foreground"> MB / {quota_mb} MB</span>
                </>
              )}
            </div>
            <div className={`text-2xl font-bold font-mono ${
              level === 'critical' ? 'text-red-600' : level === 'warning' ? 'text-amber-600' : 'text-emerald-600'
            }`} data-testid="db-size-pct">
              {used_pct}%
            </div>
          </div>
          <div className="relative w-full h-3 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${barColor}`}
              style={{ width: `${Math.min(100, used_pct)}%` }}
            />
            {/* 75% and 90% marker lines */}
            <div className="absolute top-0 h-full w-px bg-amber-600/60" style={{ left: '75%' }} />
            <div className="absolute top-0 h-full w-px bg-red-600/70" style={{ left: '90%' }} />
          </div>
          <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground pt-1">
            <span>Storage: <b className="text-foreground">{storage_mb} MB</b></span>
            <span>Indexes: <b className="text-foreground">{index_mb} MB</b></span>
            <span>Collections: <b className="text-foreground">{collections_count}</b></span>
            <span>Docs: <b className="text-foreground">{objects_count?.toLocaleString?.()}</b></span>
          </div>
          {level !== 'ok' && (
            <div className={`text-xs p-2 rounded border ${
              level === 'critical' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'
            }`}>
              {level === 'critical'
                ? '🚨 Cluster is close to its quota. Writes may start failing (e.g. saving DOB, notifications, audit logs). Trim old data or upgrade the Atlas tier.'
                : '⚠ Cluster is approaching its quota. Consider trimming old logs or upgrading the Atlas tier before writes are blocked at 100%.'}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={openBreakdown} onOpenChange={setOpenBreakdown}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Top 10 Largest Collections
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto border rounded-md">
            {collectionsLoading ? (
              <div className="py-16 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading collection breakdown…
              </div>
            ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/60 sticky top-0">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold">Collection</th>
                  <th className="px-3 py-2 font-semibold text-right">Docs</th>
                  <th className="px-3 py-2 font-semibold text-right">Data (MB)</th>
                  <th className="px-3 py-2 font-semibold text-right">Storage (MB)</th>
                  <th className="px-3 py-2 font-semibold text-right">Indexes (MB)</th>
                </tr>
              </thead>
              <tbody>
                {top_collections.map((c) => (
                  <tr key={c.name} className="border-t hover:bg-muted/40">
                    <td className="px-3 py-2 font-mono text-xs">{c.name}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{c.count?.toLocaleString?.()}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs font-semibold">{c.data_mb}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{c.storage_mb}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{c.indexes_mb}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Nightly cleanup at 03:15 UTC automatically trims activity_logs, audit_logs,
            notifications, biocloud_sync_log and other high-churn collections.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DbSizeWidget;
